import {
  ApplicationProvider,
  KeycloakContext,
  Loading,
  ModulosOxyEnum
} from '@elotech/components';
import { useSingletonLoading } from 'common/hooks';
import {
  EntidadeLogadaUtils,
  assignEntidadeSegment,
  changeEntidade,
  extractEntidadeFromPath,
  setBaseHrefEntidadeCookie
} from 'common/utils';
import { useCallback } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import {
  ActuatorService,
  EloNotificationService,
  EntidadeService,
  UsuarioCloudService
} from 'service';
import {
  EntidadeActions,
  getConfigSelector,
  getEntidadeSelector,
  getUserInfoSelector
} from 'state';
import { isAuthenticated } from 'utils';

import { ProtocoloAuthProvider } from './ProtocoloAuthProvider';
import { ProtocoloKeycloakProvider } from './ProtocoloKeycloakProvider';

const ApplicationProviderDefault = ({
  notificationService,
  onSearchEntidades,
  getUserPermission,
  onGetVersaoSobreSistema,
  onGetEntidadePrincipal,
  onChangeEntidade,
  getFirebaseToken,
  children
}) => <>{children}</>;

export const AuthProvider = ({ children }) => {
  const dispatch = useDispatch();
  const { config, entidadeLogada, currentUser } = useSelector(state => ({
    config: getConfigSelector(state),
    entidadeLogada: getEntidadeSelector(state),
    currentUser: getUserInfoSelector(state)
  }));

  const [loading, setLoading] = useSingletonLoading();

  const getUserPermissions = useCallback(() => {
    return setLoading(
      () => UsuarioCloudService.getUserPermissions(),
      'user-permissions'
    );
  }, []);

  const entidadeServiceAdapter = {
    findById: EntidadeService.findById,
    findPrincipal: () =>
      EntidadeService.getEntidadePrincipalPorAmbiente(config.isCloud)
  };

  const resolveEntidadeLogada = useCallback(() => {
    return setLoading(
      () =>
        EntidadeLogadaUtils.getCurrentOrRedirectToPrincipal(
          entidadeServiceAdapter,
          undefined,
          assignEntidadeSegment
        ),
      'entidade-logada'
    );
  }, [config.isCloud]);

  const ProfileProvider = config.isCloud
    ? ProtocoloKeycloakProvider
    : ProtocoloAuthProvider;

  const ApplicationContainer = isAuthenticated()
    ? ApplicationProvider
    : ApplicationProviderDefault;

  const onChangeEntidade = entidade => {
    const entidadeNaUrl = extractEntidadeFromPath()?.idEntidade;

    if (
      entidade &&
      entidadeLogada &&
      entidade.id.toString() !== entidadeLogada.id?.toString() &&
      entidade.id.toString() !== entidadeNaUrl
    ) {
      setBaseHrefEntidadeCookie(entidade.id);
      dispatch(EntidadeActions.setEntidadeSelecionada(entidade));
      changeEntidade(entidade.id);
    }

    if (entidade && !entidadeLogada) {
      setBaseHrefEntidadeCookie(entidade.id);
      dispatch(EntidadeActions.setEntidadeSelecionada(entidade));
    }
  };
  const getTenant = tenantId => {
    return Promise.resolve({
      data: {
        identifier: config.tenant
      }
    });
  };

  const userInfo = {
    nome: currentUser.nome,
    username: currentUser.usuario
  };

  return (
    <KeycloakContext.Provider value={{ userInfo }}>
      <ProfileProvider>
        <ApplicationContainer
          onChangeEntidade={onChangeEntidade}
          onGetEntidadePrincipal={resolveEntidadeLogada}
          disableNotification={!config.notificacaoHabilitada}
          eloNotificationService={EloNotificationService}
          onSearchEntidades={() =>
            EntidadeService.findAll().then(res => ({
              ...res,
              data: res.data.map(e => ({ ...e.entidade }))
            }))
          }
          getUserPermission={config.isCloud ? getUserPermissions : undefined}
          onGetVersaoSobreSistema={ActuatorService.getInfo}
          getTenantData={getTenant}
          tenantIdentifier={!config.isCloud ? config.tenant : undefined}
          modulo={ModulosOxyEnum.OXY_PROCESSOS}
          userProfileStandalone={
            !config.isCloud
              ? { username: currentUser.usuario, name: currentUser.nome }
              : undefined
          }
        >
          {loading ? <Loading loading /> : children}
        </ApplicationContainer>
      </ProfileProvider>
    </KeycloakContext.Provider>
  );
};
